import {useCallback, useEffect, useMemo, useState} from "react";
import {Outlet, useNavigate, useOutletContext} from "react-router-dom";
import {getAdminCountryScope} from "../../shared/api/countryScope";
import {i18n} from "../../shared/i18n";


export const DEFAULT_FILTERS = Object.freeze({
    status: "under_moderation",
    flow_type: "",
    search: "",
    submitted_from: "",
    submitted_to: "",
});

const freshDefaultFilters = () => ({...DEFAULT_FILTERS});

const normalizeFilters = (filters) => ({
    status: filters.status,
    flow_type: filters.flow_type,
    search: filters.search.trim().slice(0, 120),
    submitted_from: filters.submitted_from,
    submitted_to: filters.submitted_to,
});

const validateDateRange = ({submitted_from: submittedFrom, submitted_to: submittedTo}) =>
    submittedFrom && submittedTo && submittedFrom > submittedTo
        ? i18n.t("sections.onboarding.p42")
        : "";

export const BusinessOnboardingWorkspace = ({initialCountryScope}) => {
    const navigate = useNavigate();
    const [countryScope, setCountryScope] = useState(
        () => getAdminCountryScope() ?? initialCountryScope ?? null,
    );
    const [draftFilters, setDraftFilters] = useState(freshDefaultFilters);
    const [appliedFilters, setAppliedFilters] = useState(freshDefaultFilters);
    const [filterError, setFilterError] = useState("");
    const [page, setPage] = useState(1);

    const applyFilters = useCallback(() => {
        const normalized = normalizeFilters(draftFilters);
        const error = validateDateRange(normalized);
        setFilterError(error);
        if (error) {
            return;
        }
        setDraftFilters(normalized);
        setAppliedFilters(normalized);
        setPage(1);
    }, [draftFilters]);

    const resetFilters = useCallback(() => {
        setDraftFilters(freshDefaultFilters());
        setAppliedFilters(freshDefaultFilters());
        setFilterError("");
        setPage(1);
    }, []);

    useEffect(() => {
        const handleCountryChange = (event) => {
            const nextCountryScope = event.detail?.countryId;
            setCountryScope(nextCountryScope == null ? null : String(nextCountryScope));
            setDraftFilters(freshDefaultFilters());
            setAppliedFilters(freshDefaultFilters());
            setFilterError("");
            setPage(1);
            navigate("/business-onboarding", {replace: true});
        };
        window.addEventListener("sorpa-admin-country-scope-change", handleCountryChange);
        return () =>
            window.removeEventListener("sorpa-admin-country-scope-change", handleCountryChange);
    }, [navigate]);

    const context = useMemo(
        () => ({
            draftFilters,
            setDraftFilters,
            appliedFilters,
            applyFilters,
            resetFilters,
            filterError,
            page,
            setPage,
            countryScope,
        }),
        [
            appliedFilters,
            applyFilters,
            countryScope,
            draftFilters,
            filterError,
            page,
            resetFilters,
        ],
    );

    return <Outlet context={context} />;
};

export const useBusinessOnboardingWorkspace = () => useOutletContext();
